import { ProcessingOverlay } from '@components'

import { Color, Flex } from '@billy/design-system'
import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoicingSettingsHost } from '../../../../contexts/invoicingSettingsHostContext'
import { InvoicingSettingsPreview } from '../../../InvoicingSettingsPreview/InvoicingSettingsPreview'
import { isSelfSavingInvoicingSettingsSection } from '../../utils/isSelfSavingInvoicingSettingsSection'
import { InvoicingSettingsModalCloseButton } from '../InvoicingSettingsModalCloseButton'
import { InvoicingSettingsModalFooterActions } from '../InvoicingSettingsModalFooterActions'
import { InvoicingSettingsModalNavigation } from '../InvoicingSettingsModalNavigation'
import { InvoicingSettingsSectionContent } from '../InvoicingSettingsSectionContent'
import { useResetInvoicingSettingsFormScroll } from './hooks/useResetInvoicingSettingsFormScroll'
import * as Styled from './styles'

interface InvoicingSettingsModalContentProps {
  id: string
}

// Replaces DS `Modal.Header` / `Modal.Body` / `Modal.Footer`: their padding would keep the side nav
// off the modal edges.
export const InvoicingSettingsModalContent = ({ id }: InvoicingSettingsModalContentProps): ReactElement => {
  const { t } = useTranslation()
  const { activeSection, isProcessing } = useInvoicingSettingsHost()
  const formWrapperRef = useResetInvoicingSettingsFormScroll(activeSection)
  const isSelfSavingSection = isSelfSavingInvoicingSettingsSection(activeSection)

  return (
    <Styled.InvoicingSettingsModalContentWrapper>
      <InvoicingSettingsModalNavigation />
      <Styled.InvoicingSettingsModalMainWrapper>
        <Flex justifyContent="flex-end">
          <InvoicingSettingsModalCloseButton id={id} />
        </Flex>
        <Styled.InvoicingSettingsModalColumnsWrapper>
          <Styled.InvoicingSettingsFormWrapper ref={formWrapperRef}>
            <InvoicingSettingsSectionContent activeSection={activeSection} />
          </Styled.InvoicingSettingsFormWrapper>
          <InvoicingSettingsPreview />
        </Styled.InvoicingSettingsModalColumnsWrapper>
        {!isSelfSavingSection && (
          <Styled.InvoicingSettingsModalFooterActionsWrapper>
            <InvoicingSettingsModalFooterActions />
          </Styled.InvoicingSettingsModalFooterActionsWrapper>
        )}
      </Styled.InvoicingSettingsModalMainWrapper>
      {isProcessing && (
        <ProcessingOverlay color={Color.White} label={t('invoicing_settings.saving_changes')} withAnimation />
      )}
    </Styled.InvoicingSettingsModalContentWrapper>
  )
}
